import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { FaBriefcase, FaCalendar } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const Experience = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const experiences = [
    {
      company: 'Taxi Digital',
      positionKey: 'position',
      period: '2024',
      periodKey: 'periodCurrent',
      descriptionKey: 'experiences.taxiDigital.description',
      highlightsKey: 'experiences.taxiDigital.highlights',
      current: true,
    },
    {
      company: 'Toolzz',
      positionKey: 'position',
      period: '01/2025 - 07/2025',
      descriptionKey: 'experiences.toolzz.description',
      highlightsKey: 'experiences.toolzz.highlights',
      current: false,
    },
    {
      company: 'Atmo Digital',
      positionKey: 'position',
      period: '07/2022 - 11/2024',
      descriptionKey: 'experiences.atmoDigital.description',
      highlightsKey: 'experiences.atmoDigital.highlights',
      current: false,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  return (
    <section id="experience" className="py-20 relative">
      <div className="container mx-auto px-6" ref={ref}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          <motion.h2
            variants={itemVariants}
            className="text-4xl md:text-5xl font-bold text-center mb-16 bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent"
          >
            {t('experience.title')}
          </motion.h2>

          <div className="space-y-8 max-w-4xl mx-auto">
            {experiences.map((exp, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                className="glass p-8 rounded-2xl glow-hover relative"
              >
                {exp.current && (
                  <div className="absolute top-4 right-4 bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                    {t('experience.current')}
                  </div>
                )}

                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-blue-400 flex items-center">
                      <FaBriefcase className="mr-2" />
                      {exp.company}
                    </h3>
                    <p className="text-xl text-gray-300 mt-1">{t(`experience.${exp.positionKey}`)}</p>
                  </div>
                  <div className="flex items-center text-gray-400 mt-2 md:mt-0">
                    <FaCalendar className="mr-2" />
                    <span>{exp.period} {exp.current ? `- ${t(`experience.${exp.periodKey}`)}` : ''}</span>
                  </div>
                </div>

                <p className="text-gray-300 mb-4 leading-relaxed">{t(`experience.${exp.descriptionKey}`)}</p>

                <div className="space-y-2">
                  <p className="text-sm font-semibold text-blue-400 mb-2">{t('experience.keyAchievements')}:</p>
                  <ul className="space-y-2">
                    {(() => {
                      const translationKey = `experience.${exp.highlightsKey}`;
                      const highlights = t(translationKey, { returnObjects: true });
                      // Verifica se é um array válido
                      if (Array.isArray(highlights) && highlights.length > 0) {
                        return highlights;
                      }
                      // Fallback: retorna array vazio se não for array válido
                      return [];
                    })().map((highlight, idx) => (
                      <li key={idx} className="text-gray-300 flex items-start">
                        <span className="text-blue-500 mr-2 mt-1">▸</span>
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Experience;
